import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, DatePicker, Segmented, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { MovementKind } from '../api/types';
import { useStoreContext } from '../context/StoreContext';
import { MovementsDayDrawer, type MovementCellRef } from './movements/MovementsDayDrawer';
import { MovementsDayGridTable } from './movements/MovementsDayGridTable';

type KindFilter = MovementKind | 'ALL';

/**
 * /movements — «Перемещения» (SPEC §1) as a jadval: days × stores, each cell
 * the Σ sklad price of goods that left the store that day (returns to the
 * sklad by default, transfers via the switch); a tap on a cell opens the
 * movements behind it with links to their накладные. Admin-only.
 */
export function MovementsPage(): JSX.Element {
  const { t } = useTranslation();
  const { storeId: ctxStoreId, storeIds: ctxStoreIds } = useStoreContext();
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [kind, setKind] = useState<KindFilter>('RETURN');
  const [detail, setDetail] = useState<MovementCellRef | null>(null);

  const changeMonth = (next: Dayjs): void => {
    setDetail(null);
    setMonth(next.startOf('month'));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button icon={<LeftOutlined />} onClick={() => changeMonth(month.subtract(1, 'month'))} />
        <DatePicker
          picker="month"
          value={month}
          onChange={(m) => m && changeMonth(m)}
          allowClear={false}
          format="MMMM YYYY"
          style={{ flex: 1 }}
          inputReadOnly
        />
        <Button icon={<RightOutlined />} onClick={() => changeMonth(month.add(1, 'month'))} />
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Segmented<KindFilter>
          value={kind}
          onChange={(value) => {
            setDetail(null);
            setKind(value);
          }}
          options={[
            { value: 'RETURN', label: t('movements.kindReturn') },
            { value: 'TRANSFER', label: t('movements.kindTransfer') },
            { value: 'ALL', label: t('movements.kindAll') },
          ]}
        />
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {t('movements.gridHint')}
        </Typography.Text>
      </div>

      <MovementsDayGridTable
        month={month.format('YYYY-MM')}
        kind={kind}
        scope={{ storeId: ctxStoreId, storeIds: ctxStoreIds }}
        onCellClick={setDetail}
      />
      <MovementsDayDrawer detail={detail} kind={kind} onClose={() => setDetail(null)} />
    </div>
  );
}
